"use client";
import {
  useState,
  useEffect,
  useContext,
  createContext,
  useCallback,
  useRef,
} from "react";
import {
  // getAuth,
  onIdTokenChanged,
  updatePassword as authUpdatePassword,
  sendPasswordResetEmail as authSendPasswordResetEmail,
  confirmPasswordReset as authConfirmPasswordReset,
  reauthenticateWithCredential as authReauthenticateWithCredential,
  sendEmailVerification as authSendEmailVerification,
  signInWithCustomToken,
  signOut,
  initializeAuth,
  browserLocalPersistence,
  browserSessionPersistence,
  updateEmail as authUpdateEmail,
} from "firebase/auth";
import { resolveAuthViolation } from "./resolveAuthViolation";
import { firebaseApp } from "./firebase";
import { startOAuthProcess } from "../api/userManagement";
import { getUserRoles, getUserSubscriptionTier } from "../api/userManagement";
// import withTimeout from "./timeout";
import { createUser } from "../api/userManagement";
import { login, logout } from "../api/loginManagement";
import { linkInstallAttributionFromCookie } from "./nrsInstallAttributionLinkClient";
import { relayStoredAnalyticsConsent } from "../lib/analytics/consent-relay";
import { getGaClientIdFromBrowser } from "../lib/google/ga-cookie";
import getBrowserInfoForDb from "./getBrowserInfoForDb";
import PageLoader from "./../components/PageLoader";
import { localLogger, logger } from "./logger";
import clearLocalStorage from "./clearLocalStorage";
// import { getHealthStatus } from "../api/infrastructureManagement";

/**
 * Firebase Auth must not run `initializeAuth` during SSR: persistence uses
 * IndexedDB / DOM and throws `document is not defined`. Client modules are still
 * evaluated on the server for the initial HTML pass, so init must be lazy.
 */
let firebaseAuthSingleton = null;

function getFirebaseAuthInstance() {
  if (typeof window === "undefined") {
    return null;
  }
  if (!firebaseAuthSingleton) {
    // localStorage is prioritized over IndexedDB because Safari's Intelligent
    // Tracking Prevention can indefinitely stall the post-auth IndexedDB write
    // that Firebase performs inside signInWithCustomToken, causing the sign-in
    // promise to never resolve. localStorage is synchronous and not affected.
    // browserSessionPersistence is retained as a fallback for environments
    // where localStorage is unavailable (privacy modes, disabled storage).
    firebaseAuthSingleton = initializeAuth(firebaseApp, {
      persistence: [browserLocalPersistence, browserSessionPersistence],
    });
  }
  return firebaseAuthSingleton;
}

export const auth = new Proxy(
  {},
  {
    get(_target, prop) {
      const instance = getFirebaseAuthInstance();
      if (!instance) {
        return undefined;
      }
      const value = instance[prop];
      return typeof value === "function" ? value.bind(instance) : value;
    },
  }
);

/**
 * Firebase modular APIs must receive the real Auth instance. Passing the
 * exported `auth` Proxy can break internal SDK checks and sign-in.
 */
function firebaseAuthForSdk() {
  const instance = getFirebaseAuthInstance();
  if (!instance) {
    throw new Error("Firebase Auth is only available in the browser.");
  }
  return instance;
}

// App wrapper to provide all components with auth state & functions
const authContext = createContext(null);

export const useAuth = () => {
  const context = useContext(authContext);
  if (context === null) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export function AuthProvider({ children }) {
  const auth = useAuthProvider();
  return <authContext.Provider value={auth}>{children}</authContext.Provider>;
}

// Track user state & provide user state management functions
function useAuthProvider() {
  const [user, setUser] = useState(null);
  const userRef = useRef(null);
  const customTokenSignInInFlightRef = useRef(null);
  userRef.current = user;
  /**
   * Sign in with a Firebase custom token.
   *
   * In-flight deduplication guards against double invocation from React Strict
   * Mode remounts or rapid user interaction. If an existing session is active,
   * we sign out first so the new token replaces the prior user cleanly.
   * Always passes the real Auth instance (not the exported Proxy) to the SDK.
   */
  const signInWithCustomTokenReliable = useCallback(async (firebaseToken) => {
    if (!firebaseToken || typeof firebaseToken !== "string") {
      throw new Error("Missing Firebase custom token.");
    }
    if (customTokenSignInInFlightRef.current) {
      return customTokenSignInInFlightRef.current;
    }
    const signInPromise = (async () => {
      const fa = firebaseAuthForSdk();
      if (fa.currentUser) {
        try {
          await signOut(fa);
        } catch (e) {
          console.error("[auth] signOut before custom token failed:", e);
        }
      }
      const credential = await signInWithCustomToken(fa, firebaseToken);
      return credential.user;
    })();
    customTokenSignInInFlightRef.current = signInPromise;
    try {
      return await signInPromise;
    } finally {
      customTokenSignInInFlightRef.current = null;
    }
  }, []);


  /**
   * Stable identity: must not change every render, or consumers' useEffect(..., [refreshRoles])
   * loop forever (setUser → new refreshRoles → effect → refreshRoles → …).
   */
  const refreshRoles = useCallback(async (userCurrent = null) => {
    const thisUser = userCurrent ?? userRef.current;
    if (!thisUser) {
      return;
    }
    // Stashed user is a spread plain object: prefer live Firebase ID token (always valid for Composer).
    let token = null;
    const faRefresh = firebaseAuthForSdk();
    if (faRefresh.currentUser) {
      try {
        token = await faRefresh.currentUser.getIdToken();
      } catch (e) {
        console.error("refreshRoles: getIdToken failed", e);
        return;
      }
    }
    if (!token) {
      token = thisUser.accessToken;
    }
    if (!token) {
      console.error(
        "refreshRoles: no ID token (auth.currentUser missing and no accessToken on user)"
      );
      return;
    }
    try {
      const res = await getUserRoles(token);
      const userRoles = res.data;
      logger("roles in refresh:", userRoles);
      // Seat members only get team-user from this derived tier; a failure keeps the previous value.
      let derivedTier;
      try {
        const tierRes = await getUserSubscriptionTier(token);
        derivedTier = tierRes?.data?.subscription_tier;
      } catch (tierError) {
        console.error("Error refreshing subscription tier from Composer /users/subscription_tier:", tierError);
      }
      setUser((prev) => {
        const base = userCurrent != null ? userCurrent : prev;
        if (!base) {
          return prev;
        }
        const subscription_tier =
          typeof derivedTier === "string" && derivedTier ? derivedTier : base.subscription_tier;
        return { ...base, roles: userRoles, subscription_tier };
      });
    } catch (error) {
      const composerBase =
        process.env.NEXT_PUBLIC_COMPOSER_URL ||
        "(NEXT_PUBLIC_COMPOSER_URL unset)";
      console.error(
        "Error refreshing roles from Composer /users/roles. " +
          "If you see Load failed / CORS, verify NEXT_PUBLIC_COMPOSER_URL for this build, " +
          "that the API is reachable over HTTPS, and that this site origin is allowed in Composer CORS. " +
          "Base URL:",
        composerBase,
        error
      );
      setUser((prev) => {
        const base = userCurrent != null ? userCurrent : prev;
        if (!base) {
          return prev;
        }
        // Do not overwrite with [] on failure: that blocks claim fallbacks and "pending" gates.
        return { ...base, roles: base.roles };
      });
    }
  }, []);

  useEffect(() => {
    const unsubscribe = onIdTokenChanged(firebaseAuthForSdk(), async (firebaseUser) => {
      logger("Firebase token changed or removed");
      try {
        if (firebaseUser) {
          const token = await firebaseUser.getIdToken();
          localLogger("Token refreshed:", token);
          const idTokenResult = await firebaseUser.getIdTokenResult();
          const consentRequired = localStorage.getItem("consent_required");
          const customUser = {
            ...firebaseUser,
            consent_required: consentRequired,
            customClaims: idTokenResult.claims,
          };
          // Ensure the updated token is used in subsequent API requests
          setUser(customUser || false);
          refreshRoles(customUser);
        } else {
          logger("No Firebase user found. setting user to false");
          setUser(false);
        }
      } catch (err) {
        console.error("Error updating user:", err);
        const code = err && typeof err === "object" ? err.code : "";
        if (code === "auth/network-request-failed") {
          console.warn(
            "[auth] transient listener network failure; preserving current auth state"
          );
          return;
        }
        if (
          code === "auth/user-token-expired" ||
          code === "auth/invalid-user-token" ||
          code === "auth/invalid-credential"
        ) {
          try {
            const faListener = firebaseAuthForSdk();
            if (faListener.currentUser) {
              await signOut(faListener);
            }
          } catch (signOutErr) {
            console.error("signOut after auth listener error:", signOutErr);
          }
        }
        setUser(false);
      }
    });
    return () => unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Callable function for OAuth handler
  const callSignInWithCustomToken = async (firebaseToken, consentRequired) => {
    const u = await signInWithCustomTokenReliable(firebaseToken);
    localStorage.setItem("consent_required", consentRequired);
    const idTokenResult = u ? await u.getIdTokenResult() : { claims: {} };
    const customUser = {
      ...u,
      consent_required: consentRequired,
      customClaims: idTokenResult.claims,
    };
    setUser(customUser || false);
    return customUser;
  };

  // Sign in with email & password
  const signin = useCallback(async (email, password) => {
    try {
      // const isHealthGood = await withTimeout(
      //   getHealthStatus(),
      //   10000,
      //   "Sorry, our robots have their hands full. 🤖😓 Please try again soon!"
      // );
      // if (!isHealthGood.data) {
      //   const serviceUnavailableError = new Error(
      //     "Sorry, our robots have their hands full. 🤖😓 Please try again soon!"
      //   );
      //   serviceUnavailableError.status = 503;
      //   throw serviceUnavailableError;
      // }
      // Get browser info to send with login
      let browserInfo;
      try {
        browserInfo = await getBrowserInfoForDb();
      } catch (err) {
        console.error("Error getting browser info:", err);
      }
      // login on backend & receive custom Firebase token
      const res = await login(email, password, browserInfo);
      const firebaseToken = res.data?.token;
      localLogger("signin token:", firebaseToken);
      // sync Firebase signin on frontend & set user object
      const user = await signInWithCustomTokenReliable(firebaseToken);
      localLogger("User authenticated with Firebase:", user);
      // Save privacy consent status
      const consentRequired = res.data?.consent_required;
      localStorage.setItem("consent_required", consentRequired);
      const customUser = {
        ...user,
        consent_required: consentRequired,
      };
      setUser(customUser || false);
      const accessToken = user ? await user.getIdToken() : null;
      if (accessToken) {
        await linkInstallAttributionFromCookie(accessToken);
        void relayStoredAnalyticsConsent(
          accessToken,
          getGaClientIdFromBrowser()
        );
      }

      return customUser;
    } catch (error) {
      console.error("Signin error:", error);
      const violation = resolveAuthViolation(error, "Sign-in failed.");
      if (violation.isRegionUnsupported) {
        window.location.href = "/region-unsupported";
      }
      throw error;
    }
  }, [signInWithCustomTokenReliable]);

  // Sync user signin with Firebase after signup email auth success
  // const emailVerifiedSignin = async (firebaseToken) => {
  //   try {
  //   const auth = getAuth();
  //   await signInWithCustomToken(auth, firebaseToken);
  //   const user = auth.currentUser;
  //   logger("User authenticated with Firebase:", user);
  //   setUser(user);
  //   return user;
  // } catch (error) {
  //   console.error("Signin error:", error);
  //   throw error;
  // }
  // };

  // Create account with email & password
  const signup = async (email, password) => {
    const params = window.location.search;
    const res = await createUser(email, password, params || null);
    const user = res.data;
    // if (!user) {
    //   throw new Error("Did not receive user data.");
    // }
    setUser(user || false);
    return user;
  };

  // Sign in / sign up with provider (initiate OAuth on backend)
  const signinWithProvider = async (providerName, afterAuthPath) => {
    const params = window.location.search;
    await startOAuthProcess(providerName, params || null, afterAuthPath || "");
  };

  // Sign user out
  const signout = async () => {
    try {
      const faOut = firebaseAuthForSdk();
      if (!faOut.currentUser) {
        throw new Error("Cannot find active user to logout");
      }
      // Sign out on backend
      const accessToken = faOut.currentUser.accessToken;
      logger("Signing out from backend...");
      try {
        await logout(accessToken);
      } catch (err) {
        console.error("Backend error logging out, but proceeding:", err);
      }
      // Sync Firebase state on frontend (proceed even if fails)
      try {
        logger("Signing out from Firebase...");
        // Attempt Firebase sign out, wait only 10 seconds tops
        const timeoutPromise = new Promise((_, reject) => {
          setTimeout(() => {
            reject(new Error("Firebase signout timed out"));
          }, 10000); // 10 seconds
        });
        await Promise.race([signOut(faOut), timeoutPromise]);
      } catch (err) {
        console.error("Frontend Firebase signout failed, but proceeding:", err);
      }
      // Remove local storage items
      clearLocalStorage();
    } catch (err) {
      console.error(err);
      console.error(err.message);
      // throw err;
    } finally {
      // Remove user from state
      setUser(false);
      // eslint-disable-next-line no-unsafe-finally
      return user;
    }
  };

  // Reset password email via Firebase template (for forgotten password)
  const sendPasswordResetEmail = async (email) => {
    try {
      await authSendPasswordResetEmail(firebaseAuthForSdk(), email);
      logger("Password reset email sent successfully");
    } catch (error) {
      console.error("Error sending password reset email:", error);
      throw error;
    }
  };

  const updateEmail = async (newEmail) => {
    try {
      await authUpdateEmail(firebaseAuthForSdk().currentUser, newEmail);
      logger("Email updated successfully");
    } catch (error) {
      console.error("Error updating email:", error);
      throw error;
    }
  };

  // Confirm user completed the reset process
  const confirmPasswordReset = async (code, newPassword) => {
    try {
      await authConfirmPasswordReset(firebaseAuthForSdk(), code, newPassword);
      logger("Password has been reset successfully");
    } catch (error) {
      console.error("Error resetting password:", error);
      throw error;
    }
  };

  // Handle user changing their password in app
  const updatePassword = async (newPassword) => {
    try {
      await authUpdatePassword(firebaseAuthForSdk().currentUser, newPassword);
      logger("Password updated successfully");
    } catch (error) {
      console.error("Error updating password:", error);
      throw error;
    }
  };

  /**
   * Return the current live Firebase ID token.
   *
   * Always calls getIdToken() on the live Firebase auth current user rather than
   * reading the stashed user object, which is a spread plain object and may not
   * retain the accessToken getter from the Firebase User class.
   *
   * Returns null if no Firebase user is signed in.
   */
  const getAccessToken = async () => {
    try {
      const fa = firebaseAuthForSdk();
      if (fa.currentUser) {
        return await fa.currentUser.getIdToken();
      }
    } catch (e) {
      console.error("getAccessToken: getIdToken failed", e);
    }
    return null;
  };

  // Handle reauthentication
  const reauthenticateWithCredential = async (credential) => {
    try {
      await authReauthenticateWithCredential(
        firebaseAuthForSdk().currentUser,
        credential
      );
      logger("Reauthenticated successfully");
    } catch (error) {
      console.error("Error reauthenticating:", error);
      throw error;
    }
  };

  // Handle user changing their password in app
  const sendEmailVerification = async () => {
    try {
      await authSendEmailVerification(firebaseAuthForSdk().currentUser);
      logger("Sent email successfully");
    } catch (error) {
      console.error("Error sending verification email:", error);
      throw error;
    }
  };

  return {
    user,
    callSignInWithCustomToken,
    signin,
    // emailVerifiedSignin,
    signup,
    signinWithProvider,
    signout,
    sendPasswordResetEmail,
    confirmPasswordReset,
    reauthenticateWithCredential,
    sendEmailVerification,
    updateEmail,
    updatePassword,
    refreshRoles,
    getAccessToken,
  };
}

// Export components with this if the user must be logged in to access the component
export const requireAuth = (Component) => {
  return function RequireAuthHOC(props) {
    const auth = useAuth();
    const user = auth.user;

    useEffect(() => {
      if (user === false) {
        window.location.href = "/";
      }
    }, [user]);

    if (user === null || user === undefined) {
      return <PageLoader />;
    }

    return user ? <Component {...props} /> : null;
  };
};

/**
 * Embodiment admin gate: Composer Mongo roles when loaded; otherwise Firebase JWT claims.
 * Returns null while Composer roles have not been fetched yet (avoid redirect flash).
 * Role strings match Composer `Role` enum (hyphen) and Firebase custom claims (underscore/hyphen).
 */
export function userHasEmbodimentAdminAccess(u) {
  if (!u) {
    return false;
  }
  if (Array.isArray(u.roles)) {
    const ok =
      u.roles.includes("neuraville-embodiment-admin") ||
      u.roles.includes("neuraville-super-admin");
    if (ok) {
      return true;
    }
    if (u.roles.length > 0) {
      return false;
    }
  }
  if (u.customClaims) {
    const r = u.customClaims.role;
    if (
      r === "neuraville_embodiment_admin" ||
      r === "neuraville_super_admin" ||
      r === "neuraville-embodiment-admin" ||
      r === "neuraville-super-admin"
    ) {
      return true;
    }
  }
  if (!Array.isArray(u.roles)) {
    return null;
  }
  return false;
}

// Export components with this if the user must be an admin to access the component
export const requireAdminAuth = (Component) => {
  return function RequireAdminAuthHOC(props) {
    const { user, refreshRoles } = useAuth();

    useEffect(() => {
      if (user === false) {
        window.location.href = "/";
        return;
      }
      if (!user) {
        return;
      }
      const access = userHasEmbodimentAdminAccess(user);
      if (access === null) {
        void refreshRoles?.();
        return;
      }
      if (!access) {
        console.warn(
          "Access denied: neuraville-embodiment-admin or neuraville-super-admin required"
        );
        window.location.href = "/";
      }
    }, [user, refreshRoles]);

    if (user === null || user === undefined) {
      return <PageLoader />;
    }

    if (user === false) {
      return null;
    }

    const access = userHasEmbodimentAdminAccess(user);
    if (access === null) {
      return <PageLoader />;
    }
    if (!access) {
      return <PageLoader />;
    }

    return <Component {...props} />;
  };
};

export function userHasUserAdminAccess(u) {
  if (!u) {
    return false;
  }
  if (Array.isArray(u.roles)) {
    return (
      u.roles.includes("neuraville-user-admin") ||
      u.roles.includes("neuraville-super-admin")
    );
  }
  if (u.customClaims) {
    const r = u.customClaims.role;
    if (
      r === "neuraville_user_admin" ||
      r === "neuraville_super_admin" ||
      r === "neuraville-user-admin" ||
      r === "neuraville-super-admin"
    ) {
      return true;
    }
  }
  return null;
}

/** Neuraville super-admin only (not user-admin, genome-admin, etc.). */
export function userHasSuperAdminAccess(u) {
  if (!u) {
    return false;
  }
  if (Array.isArray(u.roles)) {
    if (u.roles.includes("neuraville-super-admin")) {
      return true;
    }
  }
  if (u.customClaims) {
    const r = u.customClaims.role;
    return r === "neuraville_super_admin" || r === "neuraville-super-admin";
  }
  return false;
}

/**
 * Super-admin gate for Composer-backed roles: returns null while roles are not loaded yet
 * (same pattern as embodiment / genome admin — avoids redirect before `/users/roles` resolves).
 */
export function userHasSuperAdminAccessState(u) {
  if (!u) {
    return false;
  }
  if (Array.isArray(u.roles)) {
    if (u.roles.includes("neuraville-super-admin")) {
      return true;
    }
    if (u.roles.length > 0) {
      return false;
    }
  }
  if (u.customClaims) {
    const r = u.customClaims.role;
    if (r === "neuraville_super_admin" || r === "neuraville-super-admin") {
      return true;
    }
  }
  if (!Array.isArray(u.roles)) {
    return null;
  }
  return false;
}

/** KPI dashboard: super-admin or delegated neuraville-kpi-admin (Composer must grant role in Mongo). */
export function userHasKpiDashboardAccess(u) {
  if (!u) {
    return false;
  }
  if (userHasSuperAdminAccess(u)) {
    return true;
  }
  if (Array.isArray(u.roles)) {
    if (u.roles.includes("neuraville-kpi-admin")) {
      return true;
    }
  }
  if (u.customClaims) {
    const r = u.customClaims.role;
    return r === "neuraville_kpi_admin" || r === "neuraville-kpi-admin";
  }
  return false;
}

/**
 * KPI gate tri-state: true / false / null (null = Composer roles not loaded yet; wait for refresh).
 */
export function userHasKpiDashboardAccessState(u) {
  if (!u) {
    return false;
  }
  if (userHasKpiDashboardAccess(u)) {
    return true;
  }
  if (!Array.isArray(u.roles)) {
    return null;
  }
  return false;
}

/**
 * Genome admin gate: Composer Mongo roles when loaded; otherwise Firebase JWT claims.
 * Returns null while Composer roles have not been fetched yet (avoid redirect flash).
 */
export function userHasGenomeAdminAccess(u) {
  if (!u) {
    return false;
  }
  if (Array.isArray(u.roles)) {
    return (
      u.roles.includes("neuraville-genome-admin") ||
      u.roles.includes("neuraville-super-admin")
    );
  }
  if (u.customClaims) {
    const r = u.customClaims.role;
    if (
      r === "neuraville_genome_admin" ||
      r === "neuraville_super_admin" ||
      r === "neuraville-genome-admin" ||
      r === "neuraville-super-admin"
    ) {
      return true;
    }
  }
  return null;
}

export const requireGenomeAdminAuth = (Component) => {
  return function RequireGenomeAdminAuthHOC(props) {
    const { user, refreshRoles } = useAuth();

    useEffect(() => {
      if (user === false) {
        window.location.href = "/";
        return;
      }
      if (!user) {
        return;
      }
      const access = userHasGenomeAdminAccess(user);
      if (access === null) {
        void refreshRoles?.();
        return;
      }
      if (!access) {
        console.warn(
          "Access denied: neuraville-genome-admin or neuraville-super-admin required"
        );
        window.location.href = "/";
      }
    }, [user, refreshRoles]);

    if (user === null || user === undefined) {
      return <PageLoader />;
    }

    if (user === false) {
      return null;
    }

    const access = userHasGenomeAdminAccess(user);
    if (access === null) {
      return <PageLoader />;
    }
    if (!access) {
      return <PageLoader />;
    }

    return <Component {...props} />;
  };
};

/**
 * Blog admin gate: neuraville-blog-admin or neuraville-super-admin.
 * Returns null while Composer roles have not been fetched yet (avoid redirect flash).
 */
export function userHasBlogAdminAccess(u) {
  if (!u) {
    return false;
  }
  if (Array.isArray(u.roles)) {
    return (
      u.roles.includes("neuraville-blog-admin") ||
      u.roles.includes("neuraville-super-admin")
    );
  }
  if (u.customClaims) {
    const r = u.customClaims.role;
    if (
      r === "neuraville_blog_admin" ||
      r === "neuraville_super_admin" ||
      r === "neuraville-blog-admin" ||
      r === "neuraville-super-admin"
    ) {
      return true;
    }
  }
  return null;
}

export const requireBlogAdminAuth = (Component) => {
  return function RequireBlogAdminAuthHOC(props) {
    const { user, refreshRoles } = useAuth();

    useEffect(() => {
      if (user === false) {
        window.location.href = "/";
        return;
      }
      if (!user) {
        return;
      }
      const access = userHasBlogAdminAccess(user);
      if (access === null) {
        void refreshRoles?.();
        return;
      }
      if (!access) {
        console.warn(
          "Access denied: neuraville-blog-admin or neuraville-super-admin required"
        );
        window.location.href = "/";
      }
    }, [user, refreshRoles]);

    if (user === null || user === undefined) {
      return <PageLoader />;
    }

    if (user === false) {
      return null;
    }

    const access = userHasBlogAdminAccess(user);
    if (access === null) {
      return <PageLoader />;
    }
    if (!access) {
      return <PageLoader />;
    }

    return <Component {...props} />;
  };
};

/**
 * Tutorial admin gate: neuraville-tutorial-admin or neuraville-super-admin.
 * Returns null while Composer roles have not been fetched yet (avoid redirect flash).
 */
export function userHasTutorialAdminAccess(u) {
  if (!u) {
    return false;
  }
  if (Array.isArray(u.roles)) {
    return (
      u.roles.includes("neuraville-tutorial-admin") ||
      u.roles.includes("neuraville-super-admin")
    );
  }
  if (u.customClaims) {
    const r = u.customClaims.role;
    if (
      r === "neuraville_tutorial_admin" ||
      r === "neuraville_super_admin" ||
      r === "neuraville-tutorial-admin" ||
      r === "neuraville-super-admin"
    ) {
      return true;
    }
  }
  return null;
}

/**
 * Badge admin gate: neuraville-badge-admin or neuraville-super-admin.
 * Returns null while Composer roles have not been fetched yet (avoid redirect flash).
 */
export function userHasBadgeAdminAccess(u) {
  if (!u) {
    return false;
  }
  if (Array.isArray(u.roles)) {
    return (
      u.roles.includes("neuraville-badge-admin") ||
      u.roles.includes("neuraville-super-admin")
    );
  }
  if (u.customClaims) {
    const r = u.customClaims.role;
    if (
      r === "neuraville_badge_admin" ||
      r === "neuraville_super_admin" ||
      r === "neuraville-badge-admin" ||
      r === "neuraville-super-admin"
    ) {
      return true;
    }
  }
  return null;
}

export const requireTutorialAdminAuth = (Component) => {
  return function RequireTutorialAdminAuthHOC(props) {
    const { user, refreshRoles } = useAuth();

    useEffect(() => {
      if (user === false) {
        window.location.href = "/";
        return;
      }
      if (!user) {
        return;
      }
      const access = userHasTutorialAdminAccess(user);
      if (access === null) {
        void refreshRoles?.();
        return;
      }
      if (!access) {
        console.warn(
          "Access denied: neuraville-tutorial-admin or neuraville-super-admin required"
        );
        window.location.href = "/";
      }
    }, [user, refreshRoles]);

    if (user === null || user === undefined) {
      return <PageLoader />;
    }

    if (user === false) {
      return null;
    }

    const access = userHasTutorialAdminAccess(user);
    if (access === null) {
      return <PageLoader />;
    }
    if (!access) {
      return <PageLoader />;
    }

    return <Component {...props} />;
  };
};

export const requireBadgeAdminAuth = (Component) => {
  return function RequireBadgeAdminAuthHOC(props) {
    const { user, refreshRoles } = useAuth();

    useEffect(() => {
      if (user === false) {
        window.location.href = "/";
        return;
      }
      if (!user) {
        return;
      }
      const access = userHasBadgeAdminAccess(user);
      if (access === null) {
        void refreshRoles?.();
        return;
      }
      if (!access) {
        console.warn(
          "Access denied: neuraville-badge-admin or neuraville-super-admin required"
        );
        window.location.href = "/";
      }
    }, [user, refreshRoles]);

    if (user === null || user === undefined) {
      return <PageLoader />;
    }

    if (user === false) {
      return null;
    }

    const access = userHasBadgeAdminAccess(user);
    if (access === null) {
      return <PageLoader />;
    }
    if (!access) {
      return <PageLoader />;
    }

    return <Component {...props} />;
  };
};

/** Partner admin: negotiate sales-partner rates (Composer Mongo role partner-admin or super-admin). */
export function userHasPartnerAdminAccess(u) {
  if (!u) {
    return false;
  }
  if (userHasSuperAdminAccess(u)) {
    return true;
  }
  if (Array.isArray(u.roles)) {
    if (u.roles.includes("partner-admin")) {
      return true;
    }
  }
  if (u.customClaims) {
    const r = u.customClaims.role;
    if (r === "partner_admin" || r === "partner-admin") {
      return true;
    }
  }
  return false;
}

export function userHasPartnerAdminAccessState(u) {
  if (!u) {
    return false;
  }
  if (userHasPartnerAdminAccess(u)) {
    return true;
  }
  if (!Array.isArray(u.roles)) {
    return null;
  }
  return false;
}

export function userHasLegalAdminAccess(u) {
  if (!u) {
    return false;
  }
  if (userHasSuperAdminAccess(u)) {
    return true;
  }
  if (Array.isArray(u.roles)) {
    if (u.roles.includes("neuraville-legal-admin")) {
      return true;
    }
  }
  if (u.customClaims) {
    const r = u.customClaims.role;
    if (r === "neuraville_legal_admin" || r === "neuraville-legal-admin") {
      return true;
    }
  }
  return false;
}

export function userHasLegalAdminAccessState(u) {
  if (!u) {
    return false;
  }
  if (userHasLegalAdminAccess(u)) {
    return true;
  }
  if (!Array.isArray(u.roles)) {
    return null;
  }
  return false;
}

/** Document administration: delegated document-admin or Neuraville super-admin. */
export function userHasDocumentAdminAccessState(u) {
  if (!u) {
    return false;
  }
  if (userHasSuperAdminAccess(u)) {
    return true;
  }
  if (Array.isArray(u.roles)) {
    if (u.roles.includes("neuraville-document-admin")) {
      return true;
    }
    if (u.roles.length > 0) {
      return false;
    }
  }
  if (u.customClaims) {
    const role = u.customClaims.role;
    if (role === "neuraville_document_admin" || role === "neuraville-document-admin") {
      return true;
    }
  }
  return Array.isArray(u.roles) ? false : null;
}

/** Email administration: delegated email-admin or Neuraville super-admin. */
export function userHasEmailAdminAccessState(u) {
  if (!u) {
    return false;
  }
  if (userHasSuperAdminAccess(u)) {
    return true;
  }
  if (Array.isArray(u.roles)) {
    if (u.roles.includes("neuraville-email-admin")) {
      return true;
    }
    if (u.roles.length > 0) {
      return false;
    }
  }
  if (u.customClaims) {
    const role = u.customClaims.role;
    if (role === "neuraville_email_admin" || role === "neuraville-email-admin") {
      return true;
    }
  }
  return Array.isArray(u.roles) ? false : null;
}

/** Bug analytics: Neuraville super-admin or neuraville-dev (matches Composer admin bugs routes). */
export function userHasBugAdminAccessState(u) {
  if (!u) {
    return false;
  }
  if (userHasSuperAdminAccess(u)) {
    return true;
  }
  if (Array.isArray(u.roles)) {
    if (u.roles.includes("neuraville-dev")) {
      return true;
    }
    if (u.roles.length > 0) {
      return false;
    }
  }
  if (u.customClaims) {
    const role = u.customClaims.role;
    if (role === "neuraville_dev" || role === "neuraville-dev") {
      return true;
    }
  }
  return Array.isArray(u.roles) ? false : null;
}

export const requireBugAdminAuth = (Component) => {
  return function RequireBugAdminAuthHOC(props) {
    const { user, refreshRoles } = useAuth();

    useEffect(() => {
      if (user === false) {
        window.location.href = "/";
        return;
      }
      if (!user) {
        return;
      }
      const access = userHasBugAdminAccessState(user);
      if (access === null) {
        void refreshRoles?.();
        return;
      }
      if (!access) {
        console.warn("Access denied: neuraville-super-admin or neuraville-dev required");
        window.location.href = "/";
      }
    }, [user, refreshRoles]);

    if (user === null || user === undefined) {
      return <PageLoader />;
    }

    if (user === false) {
      return null;
    }

    const access = userHasBugAdminAccessState(user);
    if (access !== true) {
      return <PageLoader />;
    }

    return <Component {...props} />;
  };
};

export const requirePartnerAdminAuth = (Component) => {
  return function RequirePartnerAdminAuthHOC(props) {
    const { user, refreshRoles } = useAuth();

    useEffect(() => {
      if (user === false) {
        window.location.href = "/";
        return;
      }
      if (!user) {
        return;
      }
      const access = userHasPartnerAdminAccessState(user);
      if (access === null) {
        void refreshRoles?.();
        return;
      }
      if (!access) {
        console.warn(
          "Access denied: partner-admin or neuraville-super-admin required"
        );
        window.location.href = "/";
      }
    }, [user, refreshRoles]);

    if (user === null || user === undefined) {
      return <PageLoader />;
    }

    if (user === false) {
      return null;
    }

    const access = userHasPartnerAdminAccessState(user);
    if (access === null) {
      return <PageLoader />;
    }
    if (!access) {
      return <PageLoader />;
    }

    return <Component {...props} />;
  };
};

export const requireLegalAdminAuth = (Component) => {
  return function RequireLegalAdminAuthHOC(props) {
    const { user, refreshRoles } = useAuth();

    useEffect(() => {
      if (user === false) {
        window.location.href = "/";
        return;
      }
      if (!user) {
        return;
      }
      const access = userHasLegalAdminAccessState(user);
      if (access === null) {
        void refreshRoles?.();
        return;
      }
      if (!access) {
        console.warn(
          "Access denied: neuraville-legal-admin or neuraville-super-admin required"
        );
        window.location.href = "/";
      }
    }, [user, refreshRoles]);

    if (user === null || user === undefined) {
      return <PageLoader />;
    }

    if (user === false) {
      return null;
    }

    const access = userHasLegalAdminAccessState(user);
    if (access === null) {
      return <PageLoader />;
    }
    if (!access) {
      return <PageLoader />;
    }

    return <Component {...props} />;
  };
};

/** Protect a component with the tracked-document administrator role. */
export const requireDocumentAdminAuth = (Component) => {
  return function RequireDocumentAdminAuthHOC(props) {
    const { user, refreshRoles } = useAuth();

    useEffect(() => {
      if (user === false) {
        window.location.href = "/";
        return;
      }
      if (!user) {
        return;
      }
      const access = userHasDocumentAdminAccessState(user);
      if (access === null) {
        void refreshRoles?.();
      } else if (!access) {
        console.warn(
          "Access denied: neuraville-document-admin or neuraville-super-admin required"
        );
        window.location.href = "/";
      }
    }, [user, refreshRoles]);

    if (user === null || user === undefined || user === false) {
      return user === false ? null : <PageLoader />;
    }
    return userHasDocumentAdminAccessState(user) === true
      ? <Component {...props} />
      : <PageLoader />;
  };
};

/** Protect a component with the email administrator role. */
export const requireEmailAdminAuth = (Component) => {
  return function RequireEmailAdminAuthHOC(props) {
    const { user, refreshRoles } = useAuth();

    useEffect(() => {
      if (user === false) {
        window.location.href = "/";
        return;
      }
      if (!user) {
        return;
      }
      const access = userHasEmailAdminAccessState(user);
      if (access === null) {
        void refreshRoles?.();
      } else if (!access) {
        console.warn(
          "Access denied: neuraville-email-admin or neuraville-super-admin required"
        );
        window.location.href = "/";
      }
    }, [user, refreshRoles]);

    if (user === null || user === undefined || user === false) {
      return user === false ? null : <PageLoader />;
    }
    return userHasEmailAdminAccessState(user) === true
      ? <Component {...props} />
      : <PageLoader />;
  };
};

export const requireUserAdminAuth = (Component) => {
  return function RequireUserAdminAuthHOC(props) {
    const { user, refreshRoles } = useAuth();

    useEffect(() => {
      if (user === false) {
        window.location.href = "/";
        return;
      }
      if (!user) {
        return;
      }
      const access = userHasUserAdminAccess(user);
      if (access === null) {
        void refreshRoles?.();
        return;
      }
      if (!access) {
        console.warn(
          "Access denied: neuraville-user-admin or neuraville-super-admin required"
        );
        window.location.href = "/";
      }
    }, [user, refreshRoles]);

    if (user === null || user === undefined) {
      return <PageLoader />;
    }

    if (user === false) {
      return null;
    }

    const access = userHasUserAdminAccess(user);
    if (access === null) {
      return <PageLoader />;
    }
    if (!access) {
      return <PageLoader />;
    }

    return <Component {...props} />;
  };
};

export const requireSuperAdminAuth = (Component) => {
  return function RequireSuperAdminAuthHOC(props) {
    const { user, refreshRoles } = useAuth();

    useEffect(() => {
      if (user === false) {
        window.location.href = "/";
        return;
      }
      if (!user) {
        return;
      }
      const access = userHasSuperAdminAccessState(user);
      if (access === null) {
        void refreshRoles?.();
        return;
      }
      if (!access) {
        console.warn("Access denied: neuraville-super-admin required");
        window.location.href = "/";
      }
    }, [user, refreshRoles]);

    if (user === null || user === undefined) {
      return <PageLoader />;
    }

    if (user === false) {
      return null;
    }

    const access = userHasSuperAdminAccessState(user);
    if (access === null) {
      return <PageLoader />;
    }
    if (!access) {
      return <PageLoader />;
    }

    return <Component {...props} />;
  };
};

export const requireKpiDashboardAuth = (Component) => {
  return function RequireKpiDashboardAuthHOC(props) {
    const { user, refreshRoles } = useAuth();

    useEffect(() => {
      if (user === false) {
        window.location.href = "/";
        return;
      }
      if (!user) {
        return;
      }
      const access = userHasKpiDashboardAccessState(user);
      if (access === null) {
        void refreshRoles?.();
        return;
      }
      if (!access) {
        console.warn(
          "Access denied: neuraville-super-admin or neuraville-kpi-admin required"
        );
        window.location.href = "/";
      }
    }, [user, refreshRoles]);

    if (user === null || user === undefined) {
      return <PageLoader />;
    }

    if (user === false) {
      return null;
    }

    const access = userHasKpiDashboardAccessState(user);
    if (access === null) {
      return <PageLoader />;
    }
    if (!access) {
      return <PageLoader />;
    }

    return <Component {...props} />;
  };
};

/**
 * True when the user can open at least one portal admin route.
 * Returns null while Composer roles have not been fetched yet.
 */
export function userHasAnyAdminAccessState(u) {
  if (!u) {
    return false;
  }
  const checks = [
    userHasEmbodimentAdminAccess(u),
    userHasUserAdminAccess(u),
    userHasGenomeAdminAccess(u),
    userHasBlogAdminAccess(u),
    userHasTutorialAdminAccess(u),
    userHasBadgeAdminAccess(u),
    userHasKpiDashboardAccessState(u),
    userHasPartnerAdminAccessState(u),
    userHasLegalAdminAccessState(u),
    userHasDocumentAdminAccessState(u),
    userHasEmailAdminAccessState(u),
    userHasBugAdminAccessState(u),
  ];
  if (checks.some((access) => access === true)) {
    return true;
  }
  if (checks.some((access) => access === null)) {
    return null;
  }
  return false;
}

/** Admin landing: any delegated Neuraville admin role (not public users). */
export const requireAdminLandingAuth = (Component) => {
  return function RequireAdminLandingAuthHOC(props) {
    const { user, refreshRoles } = useAuth();

    useEffect(() => {
      if (user === false) {
        window.location.href = "/";
        return;
      }
      if (!user) {
        return;
      }
      const access = userHasAnyAdminAccessState(user);
      if (access === null) {
        void refreshRoles?.();
        return;
      }
      if (!access) {
        console.warn("Access denied: no Neuraville admin roles assigned");
        window.location.href = "/";
      }
    }, [user, refreshRoles]);

    if (user === null || user === undefined) {
      return <PageLoader />;
    }

    if (user === false) {
      return null;
    }

    const access = userHasAnyAdminAccessState(user);
    if (access === null) {
      return <PageLoader />;
    }
    if (!access) {
      return <PageLoader />;
    }

    return <Component {...props} />;
  };
};

// // authUtils.js
// import { initiateOAuth, exchangeCodeForToken } from "./api"; // Import your API functions
// import { exchangeCodeForToken } from "./api"; // Import your API function
// import { getAuth, signInWithCustomToken } from "firebase/auth";

// // Function to initiate OAuth process
// export const startOAuthProcess = async (provider) => {
//   try {
//     const { oauthUrl } = await initiateOAuth(provider);
//     window.location.href = oauthUrl;
//   } catch (err) {
//     console.error("Error initiating OAuth:", err);
//     throw err; // Rethrow error to be handled by caller
//   }
// };

// // Function to handle OAuth callback
// export const handleOAuthCallback = async (code, state) => {
//   try {
//     const { firebaseToken } = await exchangeCodeForToken(code, state);

//     // Authenticate with Firebase using the custom token
//     const auth = getAuth();
//     await signInWithCustomToken(auth, firebaseToken);

//     // At this point, Firebase's user object is set and can be used throughout your app
//     const user = auth.currentUser;
//     logger("User authenticated with Firebase:", user);

//     return user;
//   } catch (err) {
//     console.error("Error handling OAuth callback:", err);
//     throw err;
//   }
// };
